import { Box, IconButton, Paper, Stack, Typography } from "@mui/material";
import CloseRounded from "@mui/icons-material/CloseRounded";
import { useState } from "react";
import SectionHeader from "./SectionHeader";
import { ArrowsExpandIcon } from "../../../../../../../components/icons/ArrowsExpandIcon";
import StreamModal from "./AddPreviousStreamsModal";

interface StreamExample {
  id?: string;
  title: string;
  url: string;
}

interface PreviousStreamsSectionProps {
  streams: StreamExample[];
  onStreamsChange: (streams: StreamExample[]) => void;
}

export default function PreviousStreamsSection({
  streams,
  onStreamsChange,
}: PreviousStreamsSectionProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [modalInitial, setModalInitial] = useState({ title: "", url: "" });

  const openAddModal = () => {
    setEditingId(null);
    setModalInitial({ title: "", url: "" });
    setModalOpen(true);
  };

  const openEditModal = (stream: StreamExample) => {
    setEditingId(stream.id ?? null);
    setModalInitial({ title: stream.title, url: stream.url });
    setModalOpen(true);
  };

  const handleModalSave = (title: string, url: string) => {
    if (editingId === null) {
      onStreamsChange([...streams, { id: String(Date.now()), title, url }]);
    } else {
      onStreamsChange(
        streams.map((s) => (s.id === editingId ? { ...s, title, url } : s)),
      );
    }
    setModalOpen(false);
  };

  const removeStream = (streamId?: string) =>
    onStreamsChange(streams.filter((s) => s.id !== streamId));

  return (
    <>
      <Paper sx={{ borderRadius: "14px", p: 2.5, boxShadow: 0, mb: 2 }}>
        <SectionHeader
          title="Previous Streams Showcase"
          action="Add Stream Example"
          onAction={openAddModal}
        />

        {streams.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 3, bgcolor: "#f8fafc", borderRadius: "10px" }}>
            <Typography sx={{ fontSize: "12px", color: "#94a3b8" }}>
              No streams yet. Add one to showcase previous work.
            </Typography>
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {streams.map((stream) => (
              <Box key={stream.id} sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                <Box
                  onClick={() => openEditModal(stream)}
                  sx={{
                    flex: 1, display: "flex", alignItems: "center", gap: 2,
                    border: "1px solid #e2e8f0", borderRadius: "10px",
                    px: 2, py: 1.5, cursor: "pointer",
                    "&:hover": { bgcolor: "#fafafa" },
                  }}
                >
                  <Box sx={{ width: 56, height: 44, borderRadius: "6px", bgcolor: "#d1d5db", flexShrink: 0 }} />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                      {stream.title}
                    </Typography>
                    <Typography sx={{ fontSize: "10px", color: "#94a3b8", mt: 0.2 }} noWrap>
                      {stream.url}
                    </Typography>
                  </Box>
                  <IconButton
                    component="a"
                    href={stream.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    sx={{ color: "#64748b", p: 0.5, "&:hover": { bgcolor: "#f0f4f8" } }}
                  >
                    <ArrowsExpandIcon sx={{ fontSize: 24, fontWeight: 900 }} />
                  </IconButton>
                </Box>
                <IconButton
                  onClick={() => removeStream(stream.id)}
                  sx={{ color: "#64748b", p: 0.5, "&:hover": { bgcolor: "#f0f4f8" } }}
                >
                  <CloseRounded sx={{ fontSize: 24 }} />
                </IconButton>
              </Box>
            ))}
          </Stack>
        )}
      </Paper>

      <StreamModal
        open={modalOpen}
        initial={modalInitial}
        mode={editingId === null ? "add" : "edit"}
        onClose={() => setModalOpen(false)}
        onSave={handleModalSave}
      />
    </>
  );
}